'use client';

import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLoginDialog } from '@/components/login-dialog';
import { useAuth } from '@/contexts/auth-context';
import { useSessionContext } from '@/contexts/session-context';
import { canManageSessions, getSessionLimit, hasReachedSessionLimit } from '@/lib/session-permissions';
import { normalizeUserAccess } from '@/lib/user-access';

export function useNewChat() {
  const router = useRouter();
  const { sessions, sessionStatus } = useSessionContext();
  const { user, isLogin } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const userAccess = useMemo(() => normalizeUserAccess(user), [user]);
  const isLocked = !canManageSessions(userAccess);
  const isSessionStateReady = !isLogin || sessionStatus === 'ready';
  const lockedTitle = isLogin ? 'Session management is unavailable for your plan' : 'Sign in to manage sessions';
  const buttonTitle = !isLogin
    ? lockedTitle
    : sessionStatus === 'error'
      ? 'Unable to verify session limit'
      : !isSessionStateReady
        ? 'Loading sessions'
        : isLocked
          ? lockedTitle
          : undefined;
  const [limitDialog, setLimitDialog] = useState<{ open: boolean; limit?: number }>({ open: false });

  const startNewChat = useCallback(
    (afterAction?: () => void) => {
      if (!isLogin) {
        openLoginDialog();
        afterAction?.();
        return;
      }

      if (!isSessionStateReady || isLocked) return;

      if (hasReachedSessionLimit(userAccess, sessions.length)) {
        setLimitDialog({ open: true, limit: getSessionLimit(userAccess) });
        return;
      }

      router.push(`/?new=${Date.now()}`);
      afterAction?.();
    },
    [isLocked, isLogin, isSessionStateReady, openLoginDialog, router, sessions.length, userAccess],
  );

  return {
    buttonTitle,
    isDisabled: isLogin && (!isSessionStateReady || isLocked),
    isLocked,
    limitDialog,
    lockedTitle,
    setLimitDialogOpen: (open: boolean) => setLimitDialog((current) => ({ ...current, open })),
    startNewChat,
  };
}
